import * as React from "react";
import { useAction, useMutation } from "convex/react";
import { FileText, Loader2, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ComboboxSelect,
  type ComboboxOption,
} from "@/components/ui/combobox-select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/convexApi";
import {
  BILL_DOCUMENT_HELP_TEXT,
  BILL_DOCUMENT_INPUT_ACCEPT,
  uploadBillDocumentDirectly,
} from "@/lib/bill-document-upload";
import { dateInputToUtcMs, parseAmountInput } from "./bill-shared";

const NO_VENDOR = "__none__";

interface VendorOption {
  _id: string;
  displayName: string;
  active: boolean;
}

export function NewBillDialog({
  authUserId,
  vendors,
  onCreated,
}: {
  authUserId: string | undefined;
  vendors: VendorOption[] | undefined;
  onCreated: (billId: string) => void;
}) {
  const createBill = useMutation(api.accountingBills.createBill);
  const createBillDocumentUploadTarget = useAction(
    (api.accountingBillDocuments as any).createBillDocumentR2UploadUrl,
  );
  const finalizeBillDocumentUpload = useAction(
    (api.accountingBillDocuments as any).finalizeBillDocumentR2Upload,
  );

  const [open, setOpen] = React.useState(false);
  const [vendorId, setVendorId] = React.useState(NO_VENDOR);
  const [invoiceNumber, setInvoiceNumber] = React.useState("");
  const [invoiceDate, setInvoiceDate] = React.useState("");
  const [dueDate, setDueDate] = React.useState("");
  const [totalAmount, setTotalAmount] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);
  // The invoice is the source of truth; typing fields is the fallback for a
  // bill with no document yet, so it stays collapsed until asked for.
  const [manualOpen, setManualOpen] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setFiles([]);
    setManualOpen(false);
    setVendorId(NO_VENDOR);
    setInvoiceNumber("");
    setInvoiceDate("");
    setDueDate("");
    setTotalAmount("");
    setNotes("");
  };

  const handleCreate = async () => {
    if (!authUserId) {
      toast.error("You are not signed in — reload the page and try again");
      return;
    }
    const amount = parseAmountInput(totalAmount);
    if (totalAmount.trim() && amount === undefined) {
      toast.error("Total must be a number");
      return;
    }
    setIsSaving(true);
    try {
      const billId = await createBill({
        authUserId,
        vendorId: vendorId === NO_VENDOR ? undefined : vendorId,
        invoiceNumber: invoiceNumber.trim() || undefined,
        invoiceDate: invoiceDate ? dateInputToUtcMs(invoiceDate) : undefined,
        dueDate: dueDate ? dateInputToUtcMs(dueDate) : undefined,
        totalAmount: amount,
        notes: notes.trim() || undefined,
      });
      // Attach the invoice before navigating: the upload is what kicks off AI
      // extraction, so the review workspace opens with suggestions already
      // in flight instead of an empty form.
      let uploadFailed = false;
      for (const file of files) {
        try {
          await uploadBillDocumentDirectly({
            billId: String(billId),
            file,
            createUploadTarget: createBillDocumentUploadTarget,
            finalizeUpload: finalizeBillDocumentUpload,
          });
        } catch (err) {
          uploadFailed = true;
          toast.error(
            err instanceof Error
              ? `${file.name}: ${err.message}`
              : `Could not attach ${file.name}`,
          );
        }
      }
      // The bill exists either way — say so plainly rather than implying the
      // whole action failed.
      toast.success(
        files.length && !uploadFailed
          ? "Bill created — reading the invoice…"
          : "Bill created",
      );
      setOpen(false);
      resetForm();
      onCreated(String(billId));
    } catch (err) {
      // Surface the real reason: a bare toast loses the Convex error detail
      // that says whether this was auth, validation, or permissions.
      console.error("createBill failed:", err);
      toast.error(err instanceof Error ? err.message : "Could not create bill");
    } finally {
      setIsSaving(false);
    }
  };

  const activeVendors = (vendors ?? []).filter((vendor) => vendor.active);
  const vendorOptions: ComboboxOption[] = [
    { value: NO_VENDOR, label: "No vendor yet" },
    ...activeVendors.map((vendor) => ({
      value: vendor._id,
      label: vendor.displayName,
    })),
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button disabled={!authUserId}>
          <Plus className="mr-2 h-4 w-4" />
          New bill
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleCreate();
          }}
          className="space-y-4"
        >
        <DialogHeader>
          <DialogTitle>New bill</DialogTitle>
          <DialogDescription>
            Attach the invoice — vendor, number, dates, total and GL coding are
            read from it once the bill opens.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={BILL_DOCUMENT_INPUT_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                if (picked.length) setFiles((prev) => [...prev, ...picked]);
                e.target.value = "";
              }}
            />
            {files.length === 0 ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="border-input text-muted-foreground hover:bg-muted/50 flex w-full items-center justify-center gap-2 rounded-md border border-dashed px-3 py-4 text-sm"
              >
                <Upload className="h-4 w-4" />
                Choose a PDF, PNG, JPEG or WebP
              </button>
            ) : (
              <div className="space-y-1">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="bg-muted/50 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm"
                  >
                    <FileText className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      className="text-muted-foreground hover:text-foreground ml-auto"
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-muted-foreground hover:text-foreground text-xs"
                >
                  Add another
                </button>
              </div>
            )}
            <p className="text-muted-foreground text-xs">
              {BILL_DOCUMENT_HELP_TEXT}
            </p>
          </div>

          {!manualOpen && (
            <button
              type="button"
              onClick={() => setManualOpen(true)}
              className="text-muted-foreground hover:text-foreground text-sm underline-offset-4 hover:underline"
            >
              No document yet? Enter the details manually
            </button>
          )}

          <div className={manualOpen ? "space-y-3" : "hidden"}>
          <div className="space-y-1.5">
            <Label htmlFor="new-bill-vendor">Vendor</Label>
            <ComboboxSelect
              id="new-bill-vendor"
              options={vendorOptions}
              value={vendorId}
              placeholder="Select vendor"
              searchPlaceholder="Type to filter vendors…"
              emptyText="No matching vendor."
              loading={vendors === undefined}
              disablePortal
              className="font-normal"
              onValueChange={(value) => setVendorId(value || NO_VENDOR)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-bill-invoice-number">Invoice #</Label>
            <Input
              id="new-bill-invoice-number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="e.g. INV-1042"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Invoice date</Label>
              <DatePicker
                value={invoiceDate}
                onChange={setInvoiceDate}
                placeholder="Invoice date"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Due date</Label>
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                placeholder="Due date"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-bill-total">Total (USD)</Label>
            <div className="relative">
              <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">
                $
              </span>
              <Input
                id="new-bill-total"
                type="text"
                inputMode="decimal"
                className="pl-7"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-bill-notes">Notes</Label>
            <Textarea
              id="new-bill-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              isSaving ||
              !authUserId ||
              (files.length === 0 && !manualOpen)
            }
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create bill
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
